import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Business as BusinessIcon,
  Code as ProjectIcon,
  Build as TechIcon,
  Schedule as TimelineIcon,
  AttachMoney as BudgetIcon,
  Edit as EditIcon,
  Send as SendIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';

/**
 * ReviewSubmitStep Component - User Story 2
 * Final step in the onboarding wizard for reviewing and submitting all information
 * 
 * Following VegaDigital Constitution: WCAG 2.1 AA compliance, comprehensive validation
 */

interface CompanyData {
  company_name?: string;
  website?: string;
  industry?: string;
  company_size?: string;
  phone?: string;
  country?: string;
  address?: string;
  description?: string;
}

interface ProjectData {
  project_type?: string;
  project_description?: string;
  features?: string[];
  platforms?: string[];
  expected_users?: string;
  performance_priority?: string;
  integration_needs?: string[];
}

interface TechnicalData {
  tech_stack?: string[];
  hosting_preference?: string;
  cms_requirement?: string;
  performance_requirements?: {
    page_load_time?: number;
    concurrent_users?: number;
    uptime_requirement?: number;
  };
  security_requirements?: string[];
  maintenance_preference?: string;
  custom_requirements?: string;
}

interface TimelineBudgetData {
  desired_timeline?: string;
  preferred_start_date?: string;
  target_launch_date?: string;
  budget_range?: string;
  budget_flexibility?: string;
  payment_preference?: string;
  priority_factors?: string[];
  rush_requirements?: string;
  additional_notes?: string;
}

interface ReviewSubmitStepProps {
  companyData: CompanyData;
  projectData: ProjectData;
  technicalData: TechnicalData;
  timelineBudgetData: TimelineBudgetData;
  onEdit: (step: number) => void;
  onSubmit: () => Promise<void>;
  onValidationChange?: (isValid: boolean) => void;
  isSubmitting?: boolean;
  submitError?: string;
}

const PROJECT_TYPE_LABELS: Record<string, string> = {
  'web-application': 'Web Application',
  'mobile-app': 'Mobile Application',
  'e-commerce': 'E-commerce Platform',
  'custom-software': 'Custom Software'
};

const FEATURE_LABELS: Record<string, string> = {
  'user-authentication': 'User Authentication & Authorization',
  'admin-dashboard': 'Admin Dashboard',
  'payment-processing': 'Payment Processing',
  'inventory-management': 'Inventory Management',
  'order-management': 'Order Management',
  'customer-accounts': 'Customer Account Management',
  'data-management': 'Data Management',
  'reporting': 'Analytics & Reporting',
  'push-notifications': 'Push Notifications',
  'offline-support': 'Offline Support',
  'camera-integration': 'Camera Integration',
  'location-services': 'Location Services',
  'workflow-automation': 'Workflow Automation',
  'data-integration': 'Data Integration',
  'custom-reporting': 'Custom Reporting',
  'api-development': 'API Development',
  'real-time-updates': 'Real-time Updates',
  'file-upload': 'File Upload & Management',
  'search-functionality': 'Advanced Search',
  'multi-language': 'Multi-language Support'
};

const PLATFORM_LABELS: Record<string, string> = {
  'web': 'Web Browser',
  'mobile-responsive': 'Mobile-Responsive Web',
  'ios': 'iOS App',
  'android': 'Android App',
  'desktop': 'Desktop Application',
  'tablet': 'Tablet Optimized'
};

const TIMELINE_LABELS: Record<string, string> = {
  'urgent': 'ASAP (Rush Project) - 2-4 weeks',
  'fast': 'Fast Track - 1-2 months',
  'standard': 'Standard Timeline - 2-4 months',
  'relaxed': 'Flexible Timeline - 4-6 months',
  'long-term': 'Long-term Project - 6+ months'
};

const BUDGET_LABELS: Record<string, string> = {
  'micro': 'Micro Project ($1,000 - $5,000)',
  'small': 'Small Project ($5,000 - $15,000)',
  'medium': 'Medium Project ($15,000 - $50,000)',
  'large': 'Large Project ($50,000 - $150,000)',
  'enterprise': 'Enterprise Project ($150,000+)',
  'discuss': 'Prefer to Discuss (Custom Quote)'
};

export const ReviewSubmitStep: React.FC<ReviewSubmitStepProps> = ({
  companyData,
  projectData,
  technicalData,
  timelineBudgetData,
  onEdit,
  onSubmit,
  onValidationChange,
  isSubmitting = false,
  submitError
}) => {
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [completionItems, setCompletionItems] = useState<string[]>([]);

  // Calculate completion status
  useEffect(() => {
    const items = [];
    
    if (companyData.company_name && companyData.industry) {
      items.push('Company information completed');
    }
    
    if (projectData.project_type && projectData.project_description) {
      items.push('Project requirements defined');
    }
    
    if (technicalData.hosting_preference) {
      items.push('Technical preferences specified');
    }
    
    if (timelineBudgetData.desired_timeline && timelineBudgetData.budget_range) {
      items.push('Timeline and budget confirmed');
    }

    setCompletionItems(items);
    
    // Notify parent of validation state - review step is valid if all sections are complete
    const isValid = items.length >= 4;
    onValidationChange?.(isValid);
  }, [companyData, projectData, technicalData, timelineBudgetData, onValidationChange]);

  const handleSubmit = async () => {
    setConfirmDialog(false);
    await onSubmit();
  };

  const isComplete = completionItems.length >= 4;

  return (
    <Box>
      <Typography variant="h6" gutterBottom color="primary">
        Review & Submit
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Please review all the information below and submit your project request. We'll get back to you within 24 hours.
      </Typography>

      {/* Completion Status */}
      <Card sx={{ mb: 3, bgcolor: isComplete ? 'success.50' : 'warning.50' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            {isComplete ? (
              <CheckIcon color="success" />
            ) : (
              <WarningIcon color="warning" />
            )}
            <Typography variant="subtitle1" color={isComplete ? 'success.main' : 'warning.main'}>
              {isComplete ? 'All sections completed!' : 'Some sections need attention'}
            </Typography>
          </Box>
          <List dense>
            {completionItems.map((item, index) => (
              <ListItem key={index} sx={{ py: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
          {!isComplete && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Please complete all sections before submitting your project request.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Company Information */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon color="primary" />
            <Typography variant="subtitle1">Company Information</Typography>
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => onEdit(0)}
              data-testid="edit-company-button"
            >
              Edit
            </Button>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {companyData.company_name && (
              <Box sx={{ flex: '1 1 300px' }}>
                <Typography variant="body2" color="text.secondary">Company Name</Typography>
                <Typography variant="body1">{companyData.company_name}</Typography>
              </Box>
            )}
            {companyData.industry && (
              <Box sx={{ flex: '1 1 300px' }}>
                <Typography variant="body2" color="text.secondary">Industry</Typography>
                <Typography variant="body1">{companyData.industry}</Typography>
              </Box>
            )}
            {companyData.company_size && (
              <Box sx={{ flex: '1 1 300px' }}>
                <Typography variant="body2" color="text.secondary">Company Size</Typography>
                <Typography variant="body1">{companyData.company_size}</Typography>
              </Box>
            )}
            {companyData.website && (
              <Box sx={{ flex: '1 1 300px' }}>
                <Typography variant="body2" color="text.secondary">Website</Typography>
                <Typography variant="body1">{companyData.website}</Typography>
              </Box>
            )}
            {companyData.country && (
              <Box sx={{ flex: '1 1 300px' }}>
                <Typography variant="body2" color="text.secondary">Country</Typography>
                <Typography variant="body1">{companyData.country}</Typography>
              </Box>
            )}
          </Box>
          {companyData.description && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">Description</Typography>
              <Typography variant="body1">{companyData.description}</Typography>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Project Requirements */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ProjectIcon color="primary" />
            <Typography variant="subtitle1">Project Requirements</Typography>
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => onEdit(1)}
              data-testid="edit-project-button"
            >
              Edit
            </Button>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {projectData.project_type && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Project Type</Typography>
              <Typography variant="body1">{PROJECT_TYPE_LABELS[projectData.project_type] || projectData.project_type}</Typography>
            </Box>
          )}
          {projectData.project_description && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Description</Typography>
              <Typography variant="body1">{projectData.project_description}</Typography>
            </Box>
          )}
          {projectData.platforms?.length && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>Target Platforms</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {projectData.platforms.map((platform) => (
                  <Chip
                    key={platform}
                    label={PLATFORM_LABELS[platform] || platform}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
          {projectData.features?.length && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>Key Features</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {projectData.features.slice(0, 6).map((feature) => (
                  <Chip
                    key={feature}
                    label={FEATURE_LABELS[feature] || feature}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                ))}
                {projectData.features.length > 6 && (
                  <Chip
                    label={`+${projectData.features.length - 6} more`}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Technical Preferences */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TechIcon color="primary" />
            <Typography variant="subtitle1">Technical Preferences</Typography>
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => onEdit(2)}
              data-testid="edit-technical-button"
            >
              Edit
            </Button>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {technicalData.hosting_preference && (
              <Box sx={{ flex: '1 1 300px' }}>
                <Typography variant="body2" color="text.secondary">Hosting Preference</Typography>
                <Typography variant="body1">{technicalData.hosting_preference.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}</Typography>
              </Box>
            )}
            {technicalData.maintenance_preference && (
              <Box sx={{ flex: '1 1 300px' }}>
                <Typography variant="body2" color="text.secondary">Maintenance Preference</Typography>
                <Typography variant="body1">{technicalData.maintenance_preference.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}</Typography>
              </Box>
            )}
          </Box>
          {technicalData.tech_stack?.length && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>Preferred Technologies</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {technicalData.tech_stack.map((tech) => (
                  <Chip
                    key={tech}
                    label={tech.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Timeline & Budget */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimelineIcon color="primary" />
            <Typography variant="subtitle1">Timeline & Budget</Typography>
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => onEdit(3)}
              data-testid="edit-timeline-button"
            >
              Edit
            </Button>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {timelineBudgetData.desired_timeline && (
              <Box sx={{ flex: '1 1 300px' }}>
                <Typography variant="body2" color="text.secondary">Timeline</Typography>
                <Typography variant="body1">{TIMELINE_LABELS[timelineBudgetData.desired_timeline] || timelineBudgetData.desired_timeline}</Typography>
              </Box>
            )}
            {timelineBudgetData.budget_range && (
              <Box sx={{ flex: '1 1 300px' }}>
                <Typography variant="body2" color="text.secondary">Budget Range</Typography>
                <Typography variant="body1">{BUDGET_LABELS[timelineBudgetData.budget_range] || timelineBudgetData.budget_range}</Typography>
              </Box>
            )}
            {timelineBudgetData.preferred_start_date && (
              <Box sx={{ flex: '1 1 300px' }}>
                <Typography variant="body2" color="text.secondary">Preferred Start Date</Typography>
                <Typography variant="body1">{new Date(timelineBudgetData.preferred_start_date).toLocaleDateString()}</Typography>
              </Box>
            )}
            {timelineBudgetData.target_launch_date && (
              <Box sx={{ flex: '1 1 300px' }}>
                <Typography variant="body2" color="text.secondary">Target Launch Date</Typography>
                <Typography variant="body1">{new Date(timelineBudgetData.target_launch_date).toLocaleDateString()}</Typography>
              </Box>
            )}
          </Box>
          {timelineBudgetData.priority_factors?.length && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>Priorities</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {timelineBudgetData.priority_factors.map((factor) => (
                  <Chip
                    key={factor}
                    label={factor.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Submit Error */}
      {submitError && (
        <Alert severity="error" sx={{ my: 3 }}>
          <Typography variant="body2">
            <strong>Submission Error:</strong> {submitError}
          </Typography>
        </Alert>
      )}

      {/* Submit Button */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
          onClick={() => setConfirmDialog(true)}
          disabled={!isComplete || isSubmitting}
          data-testid="submit-button"
          sx={{ minWidth: 200 }}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Project Request'}
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          We'll review your request and get back to you within 24 hours
        </Typography>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6">Confirm Submission</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you ready to submit your project request? Once submitted, we'll review your requirements and contact you within 24 hours with:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText primary="Initial project assessment" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText primary="Detailed proposal and timeline" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText primary="Next steps for your project" />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)} data-testid="cancel-submit-button">
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit}
            startIcon={<SendIcon />}
            data-testid="confirm-submit-button"
          >
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};